import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { observer, inject } from 'mobx-react'
import { v4 as uuidv4 } from 'uuid'
import get from 'lodash/get'

import { Droppable } from '@client/components/DnD'
import ColumnModel, {
  NULL as ColumnModelNULL,
} from '@client/components/DroppableWrapper/model'
import AppModel, { NULL as AppModelNULL } from '@client/components/App/model'
import { cancelStorage } from '@client/utils/Storage'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import ganApi from '@client/api/gan'
import { ITEM_TYPE_SURVEY } from '@client/components/Editor/settings'
import createBlockModel from '@client/components/Block/createBlockModel'
import { SECTION_TYPES } from '@client/components/Section/constants'
import { BLOCK_TYPES } from '@client/components/Block/constants'
import { reloadBlocks } from '@client/components/Sidebar/SidebarDefault/TabBuild/CustomBlocks/model'
import { showRemoteChangeToast } from '@client/components/RemoteChangeToast/model'

const PLACEHOLDER_HIDDEN = -1

export const defaultProduct = {
  type: BLOCK_TYPES.PRODUCT,
  htmlText:
    '<p data-inline="padding:0px;color:rgb(17,17,17);font-size:14px;font-family:Arial, sans-serif;font-weight:400;text-align:start;list-style-position:outside;line-height:150%; line-height: 1.5;margin: 0 0 10px"><strong>Best product</strong></p>\n<p data-inline="padding:0px;color:rgb(17,17,17);font-size:14px;font-family:Arial, sans-serif;font-weight:400;text-align:start;list-style-position:outside;line-height:150%; line-height: 1.5;margin: 0 0 10px"><span style="text-decoration: line-through;">429kr</span></p>\n<p data-inline="padding:0px;color:rgb(17,17,17);font-size:14px;font-family:Arial, sans-serif;font-weight:400;text-align:start;list-style-position:outside;line-height:150%; line-height: 1.5;margin: 0 0 10px"><span style="color: #f03e3e;">369kr</span></p>',
  settings: {
    width: '187',
    fullWidth: true,
    mobileFullWidth: true,
    imageUrl: '',
  },
}

@inject('app', 'editor')
@observer
class DroppableWrapper extends Component {
  static propTypes = {
    model: PropTypes.instanceOf(ColumnModel).isRequired,
    acceptTypes: PropTypes.array,
    app: PropTypes.instanceOf(AppModel),
    editor: PropTypes.instanceOf(EditorModel),
    children: PropTypes.element.isRequired,
    index: PropTypes.number,
  }

  constructor(props) {
    super(props)
    if (!props.model) {
      throw new Error(
        'There is no column passed to "DroppableWrapper" component!'
      )
    }
    props.model.acceptTypes = this.props.acceptTypes
    this.indexLines = null
    this.index = this.props.index
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.kind !== this.props.kind ||
      prevProps.subKind !== this.props.subKind
    ) {
      this.props.model.acceptTypes = this.props.acceptTypes
      this.indexLines = null
      this.index = this.props.index
    }
  }

  calculateIndexLines = (canDrop, el) => {
    if (canDrop) {
      this.containerTop = el.getBoundingClientRect().top

      const indexLines = Array.prototype.map.call(
        Array.prototype.filter.call(el.children, (ch, index) => {
          return (
            ch.tagName !== 'STYLE' &&
            (this.props.editor.kind !== ITEM_TYPE_SURVEY ||
              index <= el.children.length - 1)
          )
        }),
        (ch) => {
          const elRect = ch.getBoundingClientRect()

          return {
            tagName: ch.tagName,
            middle: elRect.top - this.containerTop + elRect.height / 2,
            top: elRect.top - this.containerTop,
            bottom: elRect.bottom - this.containerTop,
          }
        }
      )

      this.indexLines = indexLines

      this.indexLines.pop()
    }
  }

  onDragEnter = (event, data) => {
    const canDrop =
      !!data.counter &&
      this.props.model.typeAllowed(this.props.app.draggedComponent.type)
    this.props.model.setCanDrop(canDrop)
    this.props.model.el = data.el

    this.calculateIndexLines(canDrop, data.el)
  }

  onDragOver = (event, data) => {
    const indexLines = this.indexLines

    if (this.props.model.canDrop && indexLines && indexLines.length) {
      const rect = data.el.getBoundingClientRect()

      const mousePos = event.pageY - rect.top
      const maxLine = indexLines[indexLines.length - 1].middle
      this.index =
        mousePos < maxLine
          ? indexLines.findIndex((item) => item.middle > mousePos)
          : indexLines.length
      const placeholderPosition =
        this.index === indexLines.length
          ? this.props.editor.kind !== ITEM_TYPE_SURVEY
            ? indexLines[indexLines.length - 1].bottom
            : indexLines[indexLines.length - 1].top
          : indexLines[this.index].top

      this.props.model.setPlaceholderPosition(placeholderPosition)
    }
  }

  onDragLeave = (event, data) => {
    this.props.model.setCanDrop(false)
    this.props.model.setPlaceholderPosition(PLACEHOLDER_HIDDEN)
  }

  // Hides the drop placeholder, after a drop or when one is called off.
  cancelDrop = () => {
    const { model } = this.props
    model.setCanDrop(false)
    this.indexLines = null
    model.setPlaceholderPosition(PLACEHOLDER_HIDDEN)
  }

  onDrop = async (event, data) => {
    const { model, app, editor } = this.props
    const draggedComponent = app.draggedComponent

    if (draggedComponent.blockId) {
      let response
      try {
        response = await ganApi.getBlock(draggedComponent.blockId)
      } catch (e) {
        // Insert nothing: without the saved content the drop would add an
        // empty default block of that type (FE-1489). A 404 means the saved
        // content was deleted elsewhere, so the stale list is reloaded.
        this.cancelDrop()
        const gone = e.status === 404
        showRemoteChangeToast({
          messageKey: gone
            ? 'tabbuild.addcustom.drop-gone'
            : 'tabbuild.addcustom.drop-error',
          warning: true,
        })
        if (gone) {
          reloadBlocks()
        }
        return
      }
      if (response) {
        let data = get(response, 'block')
        if (get(response, 'block.inputName')) {
          data = Object.assign({}, response.block, { inputName: uuidv4() })
        }
        draggedComponent.restore(data)
      }
    }

    if (
      draggedComponent.sectionType === SECTION_TYPES.PRODUCTSTHREECOL ||
      draggedComponent.sectionType === SECTION_TYPES.PRODUCTSTWOCOL
    ) {
      draggedComponent.columns.map((column) => {
        // if the column is empty, add the default product
        if (column?.items?.length === 0) {
          column.addItemAtTheEnd(createBlockModel(defaultProduct, editor))
        }
      })
    }

    const container = draggedComponent.container

    if (!container) {
      const item = this.props.editor.serialize()
      cancelStorage.saveItem(item)
    }

    let currentIndex
    if (container) {
      currentIndex = container.indexOf(draggedComponent)
      container.removeItem(draggedComponent)
    }

    let index = this.index
    if (
      (container === model &&
        currentIndex <= this.index &&
        typeof currentIndex !== 'undefined') ||
      (this.props.editor.kind === ITEM_TYPE_SURVEY &&
        this.index === get(this.indexLines, 'length'))
    ) {
      index = index - 1
      if (
        this.props.editor.kind === ITEM_TYPE_SURVEY &&
        this.index === get(this.indexLines, 'length') &&
        typeof currentIndex !== 'undefined'
      ) {
        index = index - 1
      }
    }

    draggedComponent.setShowToolbar(false)
    model.addItemAt(draggedComponent, index)
    this.cancelDrop()
    if (!container) {
      app.setEditingSettings(draggedComponent)
    }
  }

  render() {
    return (
      <Droppable
        onDrop={this.onDrop}
        onDragEnter={this.onDragEnter}
        onDragLeave={this.onDragLeave}
        onDragOver={this.onDragOver}
        canDrop={this.props.model.canDrop}
      >
        {this.props.children}
      </Droppable>
    )
  }
}

export default DroppableWrapper
